import React, { useEffect, useState, useRef, useReducer, FC } from "react";
import { Row, Col, Input, Button, Popover, Switch, Image } from "antd";
import { useMediaQuery } from "react-responsive";
import { ReactSVG } from "react-svg";
import SettingsPop from "./SettingsPop";
import FilterPop from "./FilterPop";
import PinPop from "./PinPop";
import { useDispatch, useSelector } from "react-redux";
import Geocode from "react-geocode";
import FilterIcon from "assets/SVGs/FilterIcon";
import SettingsIcon from "assets/SVGs/SettingsIcon";
import CompassIcon from "assets/SVGs/CompassIcon";
import { PAGE_TYPES } from "utils/PageTypes";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import { geocodeByPlaceId } from "react-google-places-autocomplete";
import MapService from "./MapService";
import GPSSocketService from "./GPSSocketService";
import Map from "./Map";
import { RootState } from "reducers/rootReducer";
import Loader from "react-loader-spinner";
import { ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { SocketTopics } from "../../../constants/enums";
import { formatForRedis } from "utils/appUtil";
import { getFriendUserInfo } from "utils/chatUtils";
import { FilterOption } from "utils/PageTypes";
import PageFilterBar from "components/Dashboard/MessageTypes/PageFilterBar";

const DEFAULT_LOCATION = { lat: 38.8938672, lng: -77.0846158 };

function useMounted() {
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);
  return isMounted;
}

const MapPanel: FC<{ socket: ISocket; params: DashboardParams }> = ({
  socket,
  params,
}) => {
  const { screens, setScreens } = params;
  const isMobileScreen = useMediaQuery({ query: "(max-width: 700px)" });
  const { selectedItem, mapType } = useSelector(
    (state: RootState) => state.search
  );
  const { lastLocation } = useSelector((state: RootState) => state.auth);
  const {
    gps,
    subscriptions,
    map_places_bounds,
    selected_places,
    enabledFilter,
  } = useSelector((state: RootState) => state.dashboard);

  const { userBds } = useSelector((state: RootState) => state.auth);
  const { projects } = useSelector((state: RootState) => state.dashboard);
  const [latestUpdateImei, setLatestUpdatedImei] = useState<UpdateImei>();
  const [subscribedImeis, setSubscribedImeis] = useState<UpdateImei[]>([]);
  const [activePop, setActivePop] = useState<string>();
  const [currentLocation, setCurrentLocation] = useState<LocationObj>();
  const [active, setActive] = useState<boolean>(true);
  const [addressComponent, setAddressComponent] = useState<Address>();
  const [showMap, setShowMap] = useState<boolean>(true);
  const [markers, setMarkers] = useState<Markers[]>([]);
  const [gpsMarkers, setGpsMarkers] = useState<any | undefined>({});
  const [polygons, setPolygons] = useState<any>([]);
  const [circles, setCircles] = useState<any>([]);
  const [panel, setPanel] = useState<boolean>(false);
  const [enableMapSearch, setEnableMapSearch] = useState<any>({
    enable: false,
    height: undefined,
    width: undefined,
    center: undefined,
  });
  const [mapSearching, setMapSearching] = useState<boolean>(false);

  const { mode } = useSelector((state: RootState) => state.dashboard);
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);
  const pageId = useSelector(
    (state: RootState) => state.search.selectedItem,
    (a, b) => {
      return a?.pageId === b?.pageId;
    }
  )?.pageId;

  const [settings, setSetting] = useState<SettingsObj>({
    track: "auto",
    breadcrumbs: ["Show breadcrumbs"],
  });
  const [lock, setLock] = useState<string>();
  const [lockTracking, setLockTracking] = useState<LockTracking>({
    checked: false,
  });
  const {
    showGPSHistorical,
    createPeopleMarker,
    generateUniqueColor,
    createPlaceMarker,
  } = MapService();
  const { subscribeToImeis, unsubscribeToImeis } = GPSSocketService(socket);
  const dispatch = useDispatch();
  const isMounted = useMounted();
  const { allUsers } = useSelector((state: RootState) => state.auth);

  const type =
    selectedItem &&
    selectedItem.type &&
    selectedItem.type === PAGE_TYPES.BROADCAST
      ? "orange"
      : undefined;
  const color = type && type === "orange" ? "#FF5F25" : "#508AFA";

  useEffect(() => {
    if (isMounted) {
      clearMapHandler();
      getMyLocationHandler();
    }
  }, [pageId]);

  useEffect(() => {
    clearMapHandler();
    if (mapType != "HOME") {
      getMyLocationHandler();
    }
  }, [mapType]);

  useEffect(() => {
    if (isMounted) {
      if (mapType == "HOME") {
        (async function () {
          let filteredPlaces =
            (await Promise.all<any>(
              selected_places?.items?.map(async (obj, idx) => {
                return await createPlaceMarker(obj, selected_places.fitBounds);
              })
            )) ?? [];

          filteredPlaces = filteredPlaces.filter((x) => {
            return x.lat && x.lng;
          });
          setMarkers(filteredPlaces);
        })();
      }
      if (mapType == "PAGE") {
        let filteredMediaPlaces = selected_places?.items.filter((e) => {
          return (
            e.objectType === FilterOption.MEDIA || e.objectType === "medium"
          );
        });
        let filteredLocationPlaces = selected_places?.items.filter((e) => {
          return e.objectType === FilterOption.PLACE;
        });
        let filteredPeoplePlaces = selected_places?.items.filter((e) => {
          return e.objectType === FilterOption.USER;
        });

        let filteredPeoplePlacesDeviceKeys = selected_places?.items.map((e) => {
          return e.data?.iotTopic;
        });

        //think about it

        const imeisToUnsubscribe = [];
        const currentImeis = new Set<any>(subscribedImeis);

        filteredPeoplePlaces?.map((e) => {
          if (!currentImeis.has(e?.data?.iotTopic))
            showGPSHistorical(
              e,
              e?.data?.startIotAt,
              new Date().getTime(),
              true
            );
        });

        let gpsMarkersTemp = { ...gpsMarkers };
        for (let key of Object.keys(gpsMarkers)) {
          if (!filteredPeoplePlacesDeviceKeys?.includes(key)) {
            delete gpsMarkersTemp[key];
            imeisToUnsubscribe.push(key);
            currentImeis.delete(key);
          }
        }
        unsubscribeToImeis(imeisToUnsubscribe);
        setGpsMarkers(gpsMarkersTemp);
        setSubscribedImeis(Array.from(currentImeis));

        let markers: Markers[] = [];
        let polygons: any = [];
        let circles: any = [];

        filteredLocationPlaces?.map((e: any) => {
          if (e?.data?.display?.type === "polygon") {
            polygons.push(getGeometry(e));
          } else if ((e?.data?.display?.type ?? "marker") === "marker") {
            markers.push(getGeometry(e, selected_places?.fitBounds));
          } else if (e?.data?.display?.type === "circle") {
            circles.push(getGeometry(e));
          }
        });

        (async function () {
          let filteredPlaces =
            (await Promise.all<any>(
              filteredMediaPlaces?.map(async (obj, idx) => {
                return await createPlaceMarker(obj, selected_places?.fitBounds);
              })
            )) ?? [];
          filteredPlaces = filteredPlaces.filter((x) => {
            return x.lat && x.lng;
          });

          setMarkers([...filteredPlaces, ...markers]);
          setPolygons(polygons);
          setCircles(circles);
        })();
      }
    }
  }, [selected_places]);

  const getMyLocationHandler = () => {
    if (currentLocation && currentLocation?.current) {
      setCurrentLocation({ ...currentLocation });
      return;
    }
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            lat: position?.coords?.latitude,
            lng: position?.coords?.longitude,
            current: true,
          });
          Geocode.fromLatLng(
            position?.coords?.latitude?.toString(),
            position.coords.longitude?.toString()
          ).then(
            (response) => {
              let city, country;
              for (
                let i = 0, len = response.results[0].address_components.length;
                i < len;
                i++
              ) {
                var ac = response.results[0].address_components[i];
                if (ac.types.indexOf("administrative_area_level_1") >= 0)
                  city = ac.short_name;
                if (ac.types.indexOf("country") >= 0) country = ac.short_name;
              }
              setAddressComponent({ city, country });
            },
            (error) => {
              console.error(error);
            }
          );
        },
        (error) => {
          console.log("get current location error", error);
          setCurrentLocation(DEFAULT_LOCATION);
        }
      );
    } else {
      setCurrentLocation(DEFAULT_LOCATION);
    }
  };

  const clearMapHandler = () => {
    setActive(false);
    setPanel(false);
    setMarkers([]);
    setPolygons([]);
    setCircles([]);
    setGpsMarkers({});
    unsubscribeToImeis(subscribedImeis);
    setSubscribedImeis([]);
    dispatch({ type: "CLEAR_ALL_IMEI_SUBSCRIPTION" });
    dispatch({ type: "CLEAR_ALL_FILTERS" });
    Object.keys(lastLocation).map((y) => {
      socket.unSubscribeToEvent(
        SocketTopics.SubscribePageTopics,
        lastLocation[y].imei
      );
    });
  };

  const googleSuggestHandler = (place: any) => {
    geocodeByPlaceId(place.place_id)
      .then((results) => {
        if (results[0].geometry) {
          setCurrentLocation({
            lat: results[0].geometry.location.lat(),
            lng: results[0].geometry.location.lng(),
            current: false,
          });
        }
      })
      .catch((error) => console.error(error));
  };

  const getGeometry = (message: any, fitBounds: boolean = false) => {
    if (message?.data?.display?.type === "polygon") {
      return {
        originalPageMessageId: message.originalPageMessageId,
        originalCreatedAt: message.originalCreatedAt,
        senderId: message.senderId,
        ...message.data,
        id: message.id,
        ...message.data.display,
        ...message.data.attributes,
        paths: message.data.coordinates.map((coordinate) => {
          return { lat: coordinate.lat, lng: coordinate.long };
        }),
      };
    } else if ((message?.data?.display?.type ?? "marker") === "marker") {
      return {
        lat: message.data.coordinates.lat,
        lng: message.data.coordinates.long,
        ...message.data,
        ...(message?.data?.display ?? {
          type: "marker",
          color: "blue",
        }),
        ...(message?.data?.attributes ?? {
          title: message?.data?.body,
        }),
        id: message.id,
        fitBounds,
        senderId: message.senderId,
        originalPageMessageId: message.originalPageMessageId,
        originalCreatedAt: message.originalCreatedAt,
      };
    } else if (message?.data?.display?.type === "circle") {
      return {
        radius: message.data.geofence.radius,
        lat: message.data.coordinates.lat,
        lng: message.data.coordinates.long,
        ...message.data,
        ...message.data.display,
        ...message.data.attributes,
        id: message.id,
        originalPageMessageId: message.originalPageMessageId,
        originalCreatedAt: message.originalCreatedAt,
        senderId: message.senderId,
      };
    }
  };

  useEffect(() => {
    let color = generateUniqueColor();
    if (!gps?.live && subscribedImeis.length > 0) {
      unsubscribeToImeis(subscribedImeis);
      setSubscribedImeis([]);
    }

    if (gps?.live && gps?.points?.length! > 0 && gpsMarkers[gps?.imei]) {
      color = gpsMarkers[gps?.imei]?.color;
    }
    const objs =
      gps?.points?.map((obj, idx) => {
        return createPeopleMarker(
          obj,
          color,
          (idx === 0 || idx === gps?.points?.length! - 1) &&
            gps?.points?.length! > 1 &&
            !gps.live,
          gps.live &&
            (gps?.points?.length! === 1 ||
              (idx === 0 && gps?.points?.length! > 1))
        );
      }) ?? [];

    let imei = gps.imei;
    if (!gps?.live && gps.messageId) {
      imei = gps.messageId;
    }
    let tempMarkers = [...(gpsMarkers[imei] ? gpsMarkers[imei].markers : [])];
    if (
      gps?.live &&
      objs.length === 1 &&
      (!latestUpdateImei || latestUpdateImei?.mode)
    ) {
      tempMarkers = [...objs, ...tempMarkers];
      if (tempMarkers.length > 1) {
        tempMarkers[1].fillColor = color;
        tempMarkers[1].strokeWeight = 2;
        tempMarkers[1].scale = 2;
      }
    } else tempMarkers = objs;

    // console.log('markers set test', imei, markers);
    if (imei !== undefined) {
      if (mode === "multi") {
        setGpsMarkers((prevState: any) => ({
          ...prevState,
          [imei]: { markers: tempMarkers, color },
        }));
      } else {
        setGpsMarkers({
          [imei]: { markers: tempMarkers, color },
        });
      }
      setLatestUpdatedImei({ imei, mode: gps?.live });
    }
  }, [gps]);

  useEffect(() => {
    const imeisToSubscribe = new Set();
    const imeisToUnsubscribe = new Set(subscribedImeis);
    const imeiSubscriptions = new Set(subscribedImeis);
    for (let imei of subscriptions!) {
      if (!imeiSubscriptions?.has(imei)) {
        imeisToSubscribe?.add(imei);
        imeiSubscriptions?.add(imei);
      }
      imeisToUnsubscribe.delete(imei);
    }
    for (let imei of Array.from(imeiSubscriptions)) {
      if (!subscriptions?.includes(imei)) {
        imeiSubscriptions.delete(imei);
      }
    }
    setSubscribedImeis(Array.from(imeiSubscriptions));
    subscribeToImeis(Array.from(imeisToSubscribe));
    unsubscribeToImeis(Array.from(imeisToUnsubscribe));
  }, [subscriptions]);

  const getDistanceFromLatLonInKm = (lat1, lon1, lat2, lon2) => {
    var R = 6371; // Radius of the earth in km
    var dLat = deg2rad(lat2 - lat1); // deg2rad below
    var dLon = deg2rad(lon2 - lon1);
    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(deg2rad(lat1)) *
        Math.cos(deg2rad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    var d = R * c; // Distance in km
    return d * 1000;
  };

  const deg2rad = (deg) => {
    return deg * (Math.PI / 180);
  };

  const updateSettings = (
    key: keyof SettingsObj,
    value: ValueOf<SettingsObj>
  ) => {
    setSetting((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const geoSearch = () => {
    if (enabledFilter.length === 0) return;
    Object.keys(lastLocation).map((y) => {
      socket.unSubscribeToEvent(
        SocketTopics.SubscribePageTopics,
        lastLocation[y].imei
      );
    });

    const enabledQuery = [];
    enabledFilter.map((enabled) => {
      enabledQuery.push(`type\\:${enabled === "gps" ? "iot" : enabled}`);
    });

    const request = {
      select: {
        index: {
          searchIndex: `${process.env.REACT_APP_ECO_SYSTEM}:location:feature`,
          searchQuery: `@propertyTags:{org\\:${formatForRedis(
            projects?.userServiceId
          )}}@propertyTags:{${enabledQuery.join("|")}}`,
          searchOptions: "TIMEOUT 1000",
        },
        load: {
          geoJson: "$..geoJson",
        },
      },
      groupBy: {
        properties: ["@geoJson"],
        reducers: [
          {
            func: "TOLIST",
            arguments: ["@objectId"],
            name: "objectIds",
          },
        ],
      },
      apply: {
        expression: "",
        name: "",
      },
      filter: {
        expression: "",
      },
      result: {
        pagination: {
          offset: 0,
          limit: 200,
        },
      },
      from: "latLong",
      by: "box",
      member: "location-member",
      latLong: enableMapSearch.center,
      width: enableMapSearch.width,
      height: enableMapSearch.height,
      unit: "m",
    };

    dispatch({
      type: "GET_MAP_PLACES_BOUNDS",
      payload: {
        request,
        enabledFilter,
        serviceId: userBds?.[0],
      },
    });

    setMapSearching(true);
  };

  useEffect(() => {
    setMapSearching(false);
    if (map_places_bounds) {
      let lastKnown = {};
      map_places_bounds?.map((place) => {
        if (place.objectType === "gps") {
          if (!lastKnown[place.userId]) {
            lastKnown[place.userId] = {
              ...place,
              location: {
                latitude: place?.location?.coordinates?.[1],
                longitude: place?.location?.coordinates?.[0],
              },
            };
          } else if (
            lastKnown[place.userId] &&
            place.timestamp > lastKnown[place.userId].timestamp
          ) {
            lastKnown[place.userId] = {
              ...place,
              location: {
                latitude: place?.location?.coordinates?.[1],
                longitude: place?.location?.coordinates?.[0],
              },
            };
          }
        }
      });

      dispatch({
        type: "SET_FULL_GPS_LAST_LOCATION",
        payload: lastKnown,
      });
    }
  }, [map_places_bounds]);

  const setSelectedLocations = (places: object[]) => {
    dispatch({
      type: "SET_SELECTED_PLACES",
      payload: { items: places, fitBounds: activePop === "filters" },
    });
  };

  useEffect(() => {
    if (lastLocation) {
      let filteredGps = selected_places.items.filter((e: any) => {
        return e.objectType === "gps";
      });

      filteredGps.map((x: any) => {
        Object.keys(lastLocation).map((y) => {
          if (x.id === y) {
            x.location.coordinates = [
              lastLocation[y].location.longitude,
              lastLocation[y].location.latitude,
            ];
          }
        });
      });

      Object.keys(lastLocation).map((y) => {
        socket.subscribeToEvent(
          SocketTopics.SubscribePageTopics,
          lastLocation[y].imei
        );
      });

      let filteredOther = selected_places.items.filter((e: any) => {
        return e.objectType !== "gps";
      });

      let temp: object[] = [...(filteredOther || [])];
      temp = [...temp, ...(filteredGps || [])];
      setSelectedLocations(temp);
    }
  }, [lastLocation]);

  return (
    <div className="main-column map-panel mr-4">
      <div className="main-content">
        {activePop === "settings" && (
          <SettingsPop
            settings={settings}
            setActivePop={setActivePop}
            updateSettings={updateSettings}
          />
        )}
        {activePop === "filters" && (
          <FilterPop
            mapType={mapType}
            setActivePop={setActivePop}
            socket={socket}
          />
        )}
        {activePop === "pin" && <PinPop setActivePop={setActivePop} />}
        <div className="main-header">
          <div className="d-flex">
            <CompassIcon color={color} size={30} />
            <h3 className="ml-3" style={{ color }}>
              {mapType === "PAGE"
                ? (selectedItem?.persistent?.static?.metadata?.title ||
                    (selectedItem?.persistent?.static?.metadata?.type ===
                      "friend" &&
                    getFriendUserInfo(
                      allUsers,
                      selectedItem?.persistent?.static?.metadata?.members?.users
                    )?.beamId
                      ? getFriendUserInfo(
                          allUsers,
                          selectedItem?.persistent?.static?.metadata?.members
                            ?.users
                        ).beamId
                      : `Page ${selectedItem?.pageId}`)) + "'s"
                : "Main"}{" "}
              IoT
            </h3>
          </div>
          <div className="d-flex">
            <ReactSVG
              src="/images/expand-icon.svg"
              className="close-icon mr-4"
              onClick={() =>
                setScreens({
                  ...screens,
                  active: screens.active ? "" : "map",
                })
              }
            />
            {!isMobileScreen && (
              <ReactSVG
                src="/images/close-icon.svg"
                className="close-icon"
                onClick={() => setScreens({ ...screens, map: false })}
              />
            )}
          </div>
        </div>
        {/*
          The type filter sits above the map's own search. Picking a bucket the
          map cannot plot swaps this whole panel for the filtered result, so the
          bar has to be inside the column it filters — FilteredPanel mounts the
          same bar in the same place, and the pill row stays put across the swap.
        */}
        <PageFilterBar />

        <div className="search-bar mt-3">
          <div className="d-flex">
            <div className="g-map-search-input">
              {addressComponent && (
                <GooglePlacesAutocomplete
                  autocompletionRequest={{}}
                  selectProps={{
                    onChange: (place: any) => {
                      if (place) googleSuggestHandler(place.value);
                    },
                    isClearable: true,
                    placeholder: "Search map",
                    // Prefixed classes let the panel stylesheet flatten
                    // react-select into the same field the other panels use.
                    classNamePrefix: "gmap-select",
                    components: {
                      IndicatorSeparator: () => null,
                      DropdownIndicator: () => (
                        <span className="g-map-search-icon">
                          <SearchOutlined />
                        </span>
                      ),
                    },
                    styles: {
                      input: (provided: any) => ({
                        ...provided,
                        fontFamily: "Nunito-Regular",
                      }),
                    },
                  }}
                  apiKey={"AIzaSyDgNagv_ozKWc6IQ75Ol4cH8fxdntOaW6M"}
                />
              )}
            </div>
            <Button
              className="beam-btn ml-2"
              onClick={() => setActivePop("settings")}
            >
              <SettingsIcon />
            </Button>
            <Button
              className="beam-btn ml-2"
              onClick={() => setActivePop("filters")}
            >
              <FilterIcon />
            </Button>
            <Button className="beam-btn ml-2" onClick={getMyLocationHandler}>
              <ReactSVG src="/images/location-icon.svg" />
            </Button>
            <Button
              className="beam-btn ml-2"
              onClick={() => {
                clearMapHandler();
                getMyLocationHandler();
              }}
            >
              <ReactSVG src="/images/reload-icon.svg" />
            </Button>
          </div>
        </div>
        <div className="main-cards">
          <Row className="map-container-layout">
            <Col span={screens.active && panel ? 6 : 0}>
              <div id="direction-panel" />
            </Col>
            <Col span={screens.active && panel ? 18 : 24}>
              {showMap && (
                <Map
                  currentLocation={currentLocation}
                  markers={markers}
                  gpsMarkers={gpsMarkers}
                  polygons={polygons}
                  circles={circles}
                  active={active}
                  setActive={setActive}
                  showPanel={setPanel}
                  settings={settings}
                  mapType={mapType}
                  mode={mode}
                  activePop={activePop}
                  getPlaces={(resp: any) => {
                    dispatch({
                      type: "SET_MAP_BOUNDS",
                      payload: {
                        ...resp,
                      },
                    });

                    if (mapType == "HOME") {
                      const { bounds, center } = resp;
                      const height = getDistanceFromLatLonInKm(
                        bounds.minLat,
                        bounds.maxLon,
                        bounds.maxLat,
                        bounds.maxLon
                      );
                      const width = getDistanceFromLatLonInKm(
                        bounds.minLat,
                        bounds.maxLon,
                        bounds.minLat,
                        bounds.minLon
                      );

                      setEnableMapSearch({
                        enable: true,
                        height,
                        width,
                        center: {
                          lat: center.lat(),
                          long: center.lng(),
                        },
                        bounds,
                      });
                    }
                  }}
                  clearPlaces={() => {
                    if (
                      mapType == "HOME" &&
                      activePop !== "filters" &&
                      markers.length > 0 &&
                      !markers?.[0]?.fitBounds
                    ) {
                      setMarkers([]);
                      dispatch({
                        type: "SET_SELECTED_PLACES",
                        payload: { items: [], fitBounds: false },
                      });
                    }
                    if (mapType === "HOME") {
                      setEnableMapSearch({
                        enable: false,
                      });
                    }
                  }}
                  lockTracking={lockTracking}
                  latestUpdateImei={latestUpdateImei}
                  toggleLock={(id: any) => {
                    if (id !== lockTracking.lock)
                      setLockTracking({ checked: false });
                    setLock(!lock ? id : null);
                  }}
                />
              )}
            </Col>
          </Row>

          {enableMapSearch.enable && mapType === "HOME" && (
            <Row className="lock-icons map-search-filter">
              {mapSearching && (
                <Col span={2}>
                  <Loader type="TailSpin" color="#fff" height={15} width={15} />
                </Col>
              )}
              <Col span={22}>Search in the visible area</Col>
              {!mapSearching && (
                <Col span={2}>
                  <ReloadOutlined
                    onClick={() => {
                      geoSearch();
                    }}
                  />
                </Col>
              )}
            </Row>
          )}

          {lock && settings.track === "auto" && (
            <Row className="lock-icons">
              <Col span={12}>Lock tracking</Col>
              <Col span={12}>
                <Switch
                  checked={lockTracking.checked}
                  onChange={(checked) => {
                    setLockTracking({ checked, lock });
                  }}
                />
              </Col>
            </Row>
          )}

          <Row className="map-ctrl-icons">
            <Col
              span={12}
              className={mode === "single" ? "ant-col-active" : ""}
              onClick={() => {
                dispatch({
                  type: "SET_MAP_MODE",
                  payload: "single",
                });
              }}
            >
              <ReactSVG src="/images/map-line-icon.svg" />
            </Col>
            <Col
              className={mode === "multi" ? "ant-col-active" : ""}
              span={12}
              onClick={() => {
                dispatch({
                  type: "SET_MAP_MODE",
                  payload: "multi",
                });
              }}
            >
              <ReactSVG src="/images/map-triple-icon.svg" />
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
};

export default MapPanel;
